/**
 * The "Create all N campaigns" pieces of the Pending changes tray (PR 24): the
 * footer button and the confirm step's body (totals + one block per campaign).
 * Presentational only; PendingTray.tsx owns the state and the run. Kept out of
 * the tray file so the markup can be tested without opening the drawer.
 */
import { AlertTriangle, Loader2, Send } from 'lucide-react'
import { fmtMoney } from '../lib/format'
import { bidWarning, campaignTotals, type CampaignConfirmRow } from './pendingTrayRows'

/** The footer button beside Apply all. */
export function CreateAllButton({ count, busy, disabled, onClick }: {
  count: number
  busy: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
      title="Creates each campaign on Amazon in turn, in the order listed"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
      Create all {count} campaigns
    </button>
  )
}

/** The confirm's totals line and the list of every campaign about to be created. */
export function CreateAllConfirmBody({ rows }: { rows: ReadonlyArray<CampaignConfirmRow> }) {
  const totals = campaignTotals(rows)
  const warned = rows.filter(r => bidWarning(r) != null).length
  return (
    <>
      <div className="px-4 py-2 text-xs border-b border-gray-200 text-gray-600" data-testid="create-all-totals">
        <strong className="text-gray-900">{totals.count} campaigns</strong>
        {' · '}
        <strong className="text-gray-900">{fmtMoney(totals.dailyBudget)}/day</strong> total budget
        {totals.unknown > 0 && ` (${totals.unknown} with no budget on record)`}
        {warned > 0 && (
          <span className="block mt-0.5 text-amber-800">
            {warned} {warned === 1 ? 'bid was' : 'bids were'} limited — check the warnings below.
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        <ul className="divide-y divide-gray-100">
          {rows.map(r => {
            const warn = bidWarning(r)
            return (
              <li key={r.id} className="p-2">
                <div className="text-xs font-mono text-gray-800 break-all">{r.name}</div>
                <div className="mt-0.5 text-xs text-gray-600">
                  {r.term} · {r.match} · bid {fmtMoney(r.bid)}
                </div>
                <div className="text-[11px] text-gray-500">
                  {r.dailyBudget != null ? `${fmtMoney(r.dailyBudget)}/day` : 'no budget on record'}
                  {r.portfolio ? ` · ${r.portfolio}` : ''}
                </div>
                {r.negate && <div className="text-[11px] text-gray-500">blocks it in its source ad group</div>}
                {warn && (
                  <div className="mt-0.5 flex items-start gap-1 text-[11px] text-amber-800">
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                    <span>{warn}</span>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </>
  )
}
